import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setApplicationSubmissionRequestBody } from '../../redux/slices/applicationSubmissionSlice';

const getDefault = (source, key) => source?.[key] || '';

const formatGender = gender => {
  if (!gender) {
    return '';
  }
  const normalized = gender.toLowerCase();
  return normalized === 'male' || normalized === 'm'
    ? 'M'
    : normalized === 'female' || normalized === 'f'
    ? 'F'
    : '';
};

const formatDate = dateStr => {
  if (!dateStr) {
    return '';
  }
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMarketingOptOut = marketingOptOut => {
  return [marketingOptOut?.email && 'Email', marketingOptOut?.smsText && 'SMSText'].filter(Boolean);
};

const getCountryCodeFromLocale = () => {
  const locale = navigator.language || 'en-GB';
  if (locale === 'en-GB') {
    return '44';
  }
  return '';
};

const isValidSpouse = spouse =>
  spouse && spouse.dateOfBirth && (spouse.forename?.trim() || spouse.surname?.trim());

const buildQAApplyRequestBody = (selectedPlan, policyHolder) => {
  const formattedDOB = formatDate(policyHolder.dateOfBirth);

  const spouse = policyHolder?.dependents?.spouse;
  const formattedSpouse = {
    title: spouse?.title,
    forename: spouse?.forename,
    surname: spouse?.surname,
    dateOfBirth: formatDate(spouse?.dateOfBirth),
    gender: formatGender(spouse?.gender),
  };

  const children =
    policyHolder?.dependents?.children?.map(child => ({
      title: child?.title,
      forename: child?.forename,
      surname: child?.surname,
      dateOfBirth: formatDate(child.dateOfBirth),
      gender: formatGender(child.gender),
    })) || [];

  const address = policyHolder.address || {};
  const bankDetails = policyHolder.bankDetails || {};

  return {
    quoteReferenceNo: selectedPlan?.healthQuoteNumber,
    quoteVersion: selectedPlan?.healthQuoteVersion,
    recaptchaToken: '',
    productCode: selectedPlan?.productCode,
    policyHolder: {
      title: policyHolder.title,
      forename: policyHolder.forename,
      surname: policyHolder.surname,
      dateOfBirth: formattedDOB,
      gender: formatGender(policyHolder.gender),
      telephoneNumber: policyHolder.telephoneNumber,
      countryCode: getCountryCodeFromLocale(),
      emailAddress: policyHolder.emailAddress,
      address: {
        addressLine1: getDefault(address, 'line1'),
        addressLine2: getDefault(address, 'line2'),
        addressLine3: getDefault(address, 'line3'),
        postCode: getDefault(address, 'postCode'),
      },
      dependents: {
        ...(isValidSpouse(spouse) && { spouse: formattedSpouse }),
        ...(children.length > 0 && { children }),
      },
    },
    bankDetails: {
      accountHolder: getDefault(bankDetails, 'accountHolder'),
      accountNumber: getDefault(bankDetails, 'accountNumber'),
      branchSortCode: getDefault(bankDetails, 'branchSortCode'),
      bankName: getDefault(bankDetails, 'bankName'),
      paymentCollectionDay: Number(bankDetails.paymentDay),
      bankAddress: {
        addressLine1: getDefault(bankDetails?.bankAddress, 'addressline1'),
        addressLine2: getDefault(bankDetails?.bankAddress, 'addressline2'),
        addressLine3: getDefault(bankDetails?.bankAddress, 'addressline3'),
        addressLine4: getDefault(bankDetails?.bankAddress, 'addressline4'),
        country: getDefault(bankDetails?.bankAddress, 'county'),
        postcode: getDefault(bankDetails?.bankAddress, 'postCode'),
      },
    },
    marketingOptOut: getMarketingOptOut(policyHolder.marketingOptOut),
  };
};

const GetQAApplyRequestBody = () => {
  const dispatch = useDispatch();

  const quoteData = useSelector(state => state.quoteData);
  const quoteResponse = useSelector(state => state.quoteResponse);

  const policyHolder = quoteData?.prospectDetails ?? {};
  const productCode = quoteData?.productCode ?? '';
  const selectedPlan = quoteResponse?.quoteDetails?.find(item => item.productCode === productCode);

  let requestBody = buildQAApplyRequestBody(selectedPlan, policyHolder);
  requestBody.declarations = quoteData?.declarations;

  useEffect(() => {
    dispatch(setApplicationSubmissionRequestBody({ ...requestBody }));
  }, [dispatch]);
};

export default GetQAApplyRequestBody;
